import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Save, Upload, Loader2, ArrowLeft, Lock, CheckCircle2, XCircle, Instagram, Linkedin, Facebook, Camera, Users, User, Phone, Globe, Building, Edit2, ChevronUp, ChevronDown, X, Plus, MapPin } from 'lucide-react';
import { sanitizeText, sanitizeName, sanitizeRichText, sanitizePhone, sanitizeUrl, sanitizeEmail, sanitizeVideoUrl } from '@/lib/sanitize';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalInput } from '@/components/ui/BrutalInput';
import { BrutalTextarea } from '@/components/ui/BrutalTextarea';
import { SimpleRichTextEditor } from '@/components/editor/SimpleRichTextEditor';
import { CopyableUrlInput } from '@/components/ui/copyable-url-input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BrutalSelect, BrutalSelectContent, BrutalSelectItem, BrutalSelectTrigger, BrutalSelectValue } from '@/components/ui/BrutalSelect';
import { BrutalMultiSelect } from '@/components/ui/BrutalMultiSelect';
import { CategorySelector } from '@/components/CategorySelector';
import { getLanguageOptions } from '@/data/languages';

import TeamMemberModal from '@/components/TeamMemberModal';
import { ImageUpload } from '@/components/ImageUpload';
import { CoverImageUpload } from '@/components/CoverImageUpload';
import { AddressPreviewMap } from '@/components/AddressPreviewMap';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

const ProfileEdit = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, isSubscriber, isPro, isPlus } = useAuth();
  const { i18n } = useTranslation();
  const [userPreferredLang, setUserPreferredLang] = useState<string>('it');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [originalSlug, setOriginalSlug] = useState("");
  
  // Stati per geocodifica e mappa
  const [geocodingStatus, setGeocodingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [mapCoords, setMapCoords] = useState<{lat: number, lng: number} | null>(null);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [editingMemberIndex, setEditingMemberIndex] = useState<number | null>(null);
  
  // Stati per gestione azienda
  const companyLogoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCompanyLogo, setUploadingCompanyLogo] = useState(false);
  const [companyGeocodingStatus, setCompanyGeocodingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [companyMapCoords, setCompanyMapCoords] = useState<{lat: number, lng: number} | null>(null);
const [companyData, setCompanyData] = useState<any>({
    id: undefined,
    slug: '',
    name: '',
    tagline_it: '',
    tagline_en: '',
    tagline_es: '',
    description_it: '',
    description_en: '',
    description_es: '',
    logo_url: '',
    cover_image_url: '',
    video_url: '',
    email: '',
    phone: '',
    whatsapp: '',
    website: '',
    address_street: '',
    address_city: '',
    address_zip: '',
    address_region: '',
    address_country: '',
    vat_number: '',
    social_instagram: '',
    social_linkedin: '',
    social_facebook: '',
    categories: [] as string[],
    languages_spoken: [] as string[],
    latitude: null as number | null,
    longitude: null as number | null
  });
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [editingTeamMember, setEditingTeamMember] = useState<any | null>(null);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  
  const [profileData, setProfileData] = useState<any>({
    first_name: '',
    last_name: '',
    business_name: '',
    slug: '',
    business_type: 'professional' as 'professional' | 'company' | 'association',
    categories: [] as string[],
    bio_it: '',
    bio_en: '',
    bio_es: '',
    description_it: '',
    description_en: '',
    description_es: '',
    phone: '',
    whatsapp: '',
    website: '',
    booking_url: '',
    social_instagram: '',
    social_linkedin: '',
    social_facebook: '',
    address_street: '',
    address_city: '',
    address_zip: '',
    address_region: '',
    address_country: 'IT',
    service_radius_km: 10,
    languages_spoken: [] as string[],
    professional_email: '',
    price_range: '',
    online_service: false,
    in_person_service: true,
    profile_image: '',
    cover_image: '',
    video_url: '',
    team_members: [] as Array<{name: string; email: string; role: string; profile_image?: string}>,
    company_tagline: '',
    company_description: '',
  });

  useEffect(() => {
    if (user) {
      loadProfile();
      loadUserPreferredLanguage();
      if (isPlus()) {
        loadCompanyData();
      }
    }
  }, [user]);

  const loadUserPreferredLanguage = async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        .from('user_preferences')
        .select('preferred_language')
        .eq('user_id', user.id)
        .single();
      
      if (data?.preferred_language) {
        setUserPreferredLang(data.preferred_language);
      } else {
        // Fallback to interface language
        setUserPreferredLang(i18n.language?.substring(0, 2) || 'it');
      }
    } catch (error) {
      // Fallback to interface language
      setUserPreferredLang(i18n.language?.substring(0, 2) || 'it');
    }
  };

  const generateCompanySlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleCompanyNameChange = (name: string) => {
    setCompanyData({
      ...companyData,
      name: name,
      slug: generateCompanySlug(name)
    });
  };

  const loadCompanyData = async () => {
    if (!user || !isPlus()) return;

    try {
      const { data, error } = await supabase.functions.invoke('get-company', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.company) {
        const company = data.company;
        // Mappa TUTTI i campi convertendo null/undefined in stringhe vuote
        setCompanyData({
          id: company.id || undefined,
          slug: company.slug || '',
          name: company.name || '',
          tagline_it: company.tagline_it || '',
          tagline_en: company.tagline_en || '',
          tagline_es: company.tagline_es || '',
          description_it: company.description_it || '',
          description_en: company.description_en || '',
          description_es: company.description_es || '',
          logo_url: company.logo_url || '',
          cover_image_url: company.cover_image_url || '',
          video_url: company.video_url || '',
          email: company.email || '',
          phone: company.phone || '',
          whatsapp: company.whatsapp || '',
          website: company.website || '',
          address_street: company.address_street || '',
          address_city: company.address_city || '',
          address_zip: company.address_zip || '',
          address_region: company.address_region || '',
          address_country: company.address_country || '',
          vat_number: company.vat_number || '',
          social_instagram: company.social_instagram || '',
          social_linkedin: company.social_linkedin || '',
          social_facebook: company.social_facebook || '',
          categories: company.categories || [],
          languages_spoken: company.languages_spoken || [],
          latitude: company.latitude || null,
          longitude: company.longitude || null,
        });
        
        // Se ci sono coordinate, inizializza la mappa
        if (company.latitude && company.longitude) {
          setCompanyMapCoords({ lat: company.latitude, lng: company.longitude });
          setCompanyGeocodingStatus('success');
        }
        setTeamMembers(data.teamMembers || []);
      }
    } catch (error: any) {
      console.error('Error loading company:', {
        error,
        message: error.message,
        status: error.status,
        details: error.details
      });
      
      const errorMessage = error?.message || error?.details || 'Errore sconosciuto';
      
      toast({
        title: t('common.error'),
        description: `Errore caricamento dati azienda: ${errorMessage}`,
        variant: 'error',
      });
    }
  };

  const handleCompanyLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    const validTypes = ["image/jpeg", "image/png", "image/svg+xml", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Formato non valido",
        description: "Usa JPG, PNG, SVG o WebP.",
        variant: "error",
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File troppo grande",
        description: "Massimo 2MB.",
        variant: "error",
      });
      return;
    }

    setUploadingCompanyLogo(true);

    try {
      if (companyData.logo_url) {
        const oldFileName = companyData.logo_url.split('/').pop();
        if (oldFileName) {
          await supabase.storage.from("platform-logos").remove([oldFileName]);
        }
      }

      const fileExt = file.name.split(".").pop();
      const fileName = `company-${user.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("platform-logos")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("platform-logos")
        .getPublicUrl(fileName);

      setCompanyData({ ...companyData, logo_url: data.publicUrl });
      toast({
        title: "Logo caricato",
        description: "Logo caricato con successo!",
      });
    } catch (error) {
      console.error("Error uploading company logo:", error);
      toast({
        title: "Errore upload",
        description: "Errore durante l'upload del logo",
        variant: "error",
      });
    } finally {
      setUploadingCompanyLogo(false);
      if (companyLogoInputRef.current) {
        companyLogoInputRef.current.value = "";
      }
    }
  };

  const handleRemoveCompanyLogo = async () => {
    if (!companyData.logo_url) return;

    try {
      const fileName = companyData.logo_url.split('/').pop();
      if (fileName) {
        await supabase.storage.from("platform-logos").remove([fileName]);
      }
      setCompanyData({ ...companyData, logo_url: "" });
      toast({
        title: "Logo rimosso",
        description: "Logo rimosso con successo!",
      });
    } catch (error) {
      console.error("Error removing company logo:", error);
      toast({
        title: "Errore",
        description: "Errore durante la rimozione del logo",
        variant: "error",
      });
    }
  };

  const saveCompanyData = async () => {
    if (!user || !companyData.name || isSavingCompany) return;

    setIsSavingCompany(true);

    try {
      // Helper per pulire stringhe vuote/null in undefined
      const cleanValue = (value: string | null | undefined): string | undefined => {
        if (value === null || value === undefined || value.trim() === '') {
          return undefined;
        }
        return value.trim();
      };

      // Sanitize all company data before sending
      const sanitizedCompanyData = {
        ...companyData,
        name: sanitizeName(companyData.name || ''),
        tagline_it: sanitizeText(companyData.tagline_it || ''),
        tagline_en: sanitizeText(companyData.tagline_en || ''),
        tagline_es: sanitizeText(companyData.tagline_es || ''),
        description_it: sanitizeRichText(companyData.description_it || ''),
        description_en: sanitizeRichText(companyData.description_en || ''),
        description_es: sanitizeRichText(companyData.description_es || ''),
        email: sanitizeEmail(companyData.email || ''),
        phone: sanitizePhone(companyData.phone || ''),
        whatsapp: sanitizePhone(companyData.whatsapp || ''),
        website: sanitizeUrl(companyData.website || ''),
        video_url: sanitizeVideoUrl(companyData.video_url || ''),
        vat_number: sanitizeText(companyData.vat_number || ''),
        address_street: sanitizeText(companyData.address_street || ''),
        address_city: sanitizeText(companyData.address_city || ''),
        address_zip: sanitizeText(companyData.address_zip || ''),
        address_region: sanitizeText(companyData.address_region || ''),
        social_instagram: sanitizeUrl(companyData.social_instagram || ''),
        social_linkedin: sanitizeUrl(companyData.social_linkedin || ''),
        social_facebook: sanitizeUrl(companyData.social_facebook || ''),
      };

      // Costruisci oggetto pulito per il backend (Zod non accetta null o stringhe vuote per URL)
      const cleanedCompanyData = {
        // Includi id solo se esiste (per update)
        ...(sanitizedCompanyData.id ? { id: sanitizedCompanyData.id } : {}),
        
        // Campi obbligatori
        slug: sanitizedCompanyData.slug,
        name: sanitizedCompanyData.name,
        
        // Campi opzionali - stringhe
        tagline_it: cleanValue(sanitizedCompanyData.tagline_it),
        tagline_en: cleanValue(sanitizedCompanyData.tagline_en),
        tagline_es: cleanValue(sanitizedCompanyData.tagline_es),
        description_it: cleanValue(sanitizedCompanyData.description_it),
        description_en: cleanValue(sanitizedCompanyData.description_en),
        description_es: cleanValue(sanitizedCompanyData.description_es),
        email: cleanValue(sanitizedCompanyData.email),
        phone: cleanValue(sanitizedCompanyData.phone),
        whatsapp: cleanValue(sanitizedCompanyData.whatsapp),
        address_street: cleanValue(sanitizedCompanyData.address_street),
        address_city: cleanValue(sanitizedCompanyData.address_city),
        address_zip: cleanValue(sanitizedCompanyData.address_zip),
        address_region: cleanValue(sanitizedCompanyData.address_region),
        address_country: cleanValue(sanitizedCompanyData.address_country),
        vat_number: cleanValue(sanitizedCompanyData.vat_number),
        social_instagram: cleanValue(sanitizedCompanyData.social_instagram),
        social_linkedin: cleanValue(sanitizedCompanyData.social_linkedin),
        social_facebook: cleanValue(sanitizedCompanyData.social_facebook),
        
        // Campi URL - puliti per evitare errori Zod
        logo_url: cleanValue(sanitizedCompanyData.logo_url),
        cover_image_url: cleanValue(sanitizedCompanyData.cover_image_url),
        video_url: cleanValue(sanitizedCompanyData.video_url),
        website: cleanValue(sanitizedCompanyData.website),
        
        // Coordinate - passate dal frontend se verificate
        latitude: companyMapCoords?.lat || sanitizedCompanyData.latitude || undefined,
        longitude: companyMapCoords?.lng || sanitizedCompanyData.longitude || undefined,
        
        // Array
        categories: sanitizedCompanyData.categories || [],
        languages_spoken: sanitizedCompanyData.languages_spoken || [],
      };

      const { data, error } = await supabase.functions.invoke('save-company', {
        body: {
          company: cleanedCompanyData,
          teamMembers: teamMembers,
        },
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      // Gestisci errori dalla edge function (es. 403 per piano non Plus)
      if (error) throw error;
      if (data?.error) {
        throw new Error(data.error);
      }

      // Aggiorna stato con dati salvati dal backend (fonte di verità)
      if (data?.companyId) {
        setCompanyData(prev => ({ 
          ...prev, 
          id: data.companyId,
          // Sincronizza i campi ritornati dal backend
          ...(data.savedFields ? {
            languages_spoken: data.savedFields.languages_spoken || [],
            cover_image_url: data.savedFields.cover_image_url || '',
            video_url: data.savedFields.video_url || '',
            categories: data.savedFields.categories || [],
            logo_url: data.savedFields.logo_url || '',
          } : {})
        }));
      }

      return true;
    } catch (error: any) {
      console.error('Error saving company:', error);
      toast({
        title: t('common.error'),
        description: error.message || 'Errore salvataggio azienda',
        variant: 'error',
      });
      return false;
    } finally {
      setIsSavingCompany(false);
    }
  };

  // Funzioni gestione team
  const handleEditTeamMember = (index: number) => {
    setEditingMemberIndex(index);
    setEditingTeamMember(teamMembers[index]);
    setShowAddTeamModal(true);
  };

  const handleRemoveTeamMember = (index: number) => {
    if (confirm('Rimuovere questo membro del team?')) {
      const newTeam = teamMembers.filter((_, i) => i !== index);
      setTeamMembers(newTeam);
    }
  };

  const handleMoveTeamMember = (index: number, direction: 'up' | 'down') => {
    const newTeam = [...teamMembers];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < teamMembers.length) {
      [newTeam[index], newTeam[newIndex]] = [newTeam[newIndex], newTeam[index]];
      // Update positions
      newTeam.forEach((member, i) => {
        member.position = i;
      });
      setTeamMembers(newTeam);
    }
  };

  const handleAddTeamMember = (memberData: any) => {
    if (editingMemberIndex !== null) {
      // Update existing member
      const newTeam = [...teamMembers];
      newTeam[editingMemberIndex] = {
        ...memberData,
        position: editingMemberIndex
      };
      setTeamMembers(newTeam);
    } else {
      // Add new member
      setTeamMembers([
        ...teamMembers,
        {
          ...memberData,
          position: teamMembers.length
        }
      ]);
    }
    setEditingMemberIndex(null);
    setEditingTeamMember(null);
  };

  // Funzione per verificare l'indirizzo azienda e mostrare mappa
  const handleVerifyCompanyAddress = async () => {
    if (!companyData.address_city || !companyData.address_country) {
      toast({
        title: t('profile.geocodingError'),
        description: 'Città e paese sono obbligatori',
        variant: "error",
      });
      return;
    }

    setCompanyGeocodingStatus('loading');
    
    try {
      const { data, error } = await supabase.functions.invoke('geocode-address', {
        body: {
          street: companyData.address_street || '',
          city: companyData.address_city,
          region: companyData.address_region || '',
          country: companyData.address_country || 'IT'
        }
      });

      if (error) {
        console.error('Company geocoding API error:', error);
        throw new Error(error.message || 'Errore durante la geocodifica');
      }

      if (data && data.latitude && data.longitude) {
        console.log('🗺️ Company address found:', data);
        
        setCompanyMapCoords({
          lat: data.latitude,
          lng: data.longitude
        });
        
        // Aggiorna anche companyData con le nuove coordinate
        setCompanyData((prev: any) => ({
          ...prev,
          latitude: data.latitude,
          longitude: data.longitude
        }));
        
        setCompanyGeocodingStatus('success');
        toast({
          title: "✅ Indirizzo localizzato",
          description: "Trascina il pin sulla mappa per posizionarlo esattamente",
        });
      } else {
        throw new Error('Nessuna coordinata ricevuta dal servizio');
      }
    } catch (error: any) {
      console.error('Company geocoding error:', error);
      setCompanyGeocodingStatus('error');
      setCompanyMapCoords(null);
      toast({
        title: t('profile.geocodingError'),
        description: error.message || 'Impossibile trovare l\'indirizzo. Verifica i dati e riprova.',
        variant: "error",
      });
    }
  };

  // Gestisce lo spostamento del pin sulla mappa azienda
  const handleCompanyMapPositionChange = (lat: number, lng: number) => {
    setCompanyMapCoords({ lat, lng });
    setCompanyData((prev: any) => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
    console.log('📍 Nuova posizione pin azienda:', { lat, lng });
  };

  // Funzione per verificare la città e mostrare mappa draggable
  const handleVerifyAddress = async () => {
    if (!profileData.address_city || !profileData.address_country) {
      toast({
        title: t('profile.geocodingError'),
        description: 'Città e paese sono obbligatori',
        variant: "error",
      });
      return;
    }

    setGeocodingStatus('loading');
    
    try {
      const { data, error } = await supabase.functions.invoke('geocode-address', {
        body: {
          city: profileData.address_city,
          region: profileData.address_region || '',
          country: profileData.address_country
        }
      });

      if (error) {
        console.error('Geocoding API error:', error);
        throw new Error(error.message || 'Errore durante la geocodifica');
      }

      if (data && data.latitude && data.longitude) {
        console.log('🗺️ City center found:', data);
        
        setMapCoords({
          lat: data.latitude,
          lng: data.longitude
        });
        
        setGeocodingStatus('success');
        toast({
          title: "✅ Città localizzata",
          description: "Trascina il pin sulla mappa per posizionarlo esattamente",
        });
      } else {
        throw new Error('Nessuna coordinata ricevuta dal servizio');
      }
    } catch (error: any) {
      console.error('Geocoding error:', error);
      setGeocodingStatus('error');
      setMapCoords(null);
      toast({
        title: t('profile.geocodingError'),
        description: error.message || 'Impossibile trovare la città. Verifica i dati e riprova.',
        variant: "error",
      });
    }
  };

  // Gestisce lo spostamento del pin sulla mappa
  const handleMapPositionChange = (lat: number, lng: number) => {
    setMapCoords({ lat, lng });
    console.log('📍 Nuova posizione pin:', { lat, lng });
  };

  const loadProfile = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        const slug = data.slug || '';
        setOriginalSlug(slug);
        setProfileData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          business_name: data.business_name || '',
          slug: slug,
          business_type: 'professional', // Always force professional
          categories: data.categories || [],
          bio_it: data.bio_it || '',
          bio_en: data.bio_en || '',
          bio_es: data.bio_es || '',
          description_it: data.description_it || '',
          description_en: data.description_en || '',
          description_es: data.description_es || '',
          phone: data.phone || '',
          whatsapp: data.whatsapp || '',
          website: data.website || '',
          booking_url: data.booking_url || '',
          social_instagram: data.social_instagram || '',
          social_linkedin: data.social_linkedin || '',
          social_facebook: data.social_facebook || '',
          address_street: data.address_street || '',
          address_city: data.address_city || '',
          address_zip: data.address_zip || '',
          address_region: data.address_region || '',
          address_country: data.address_country || '',
          service_radius_km: data.service_radius_km || 10,
          languages_spoken: data.languages_spoken || [],
          professional_email: data.professional_email || '',
          price_range: data.price_range || '',
          online_service: data.online_service || false,
          in_person_service: data.in_person_service !== false,
          profile_image: data.profile_image || '',
          cover_image: data.cover_image || '',
          video_url: data.video_url || '',
          team_members: data.team_members || [],
          company_tagline: data.company_tagline || '',
          company_description: data.company_description || '',
        });
        
        // Inizializza la mappa se esistono coordinate salvate
        if (data.latitude && data.longitude) {
          setMapCoords({ lat: data.latitude, lng: data.longitude });
          setGeocodingStatus('success');
        }
      }
    } catch (error: any) {
      console.error('Error loading profile:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const generateSlugFromName = useCallback(async (firstName: string, lastName: string) => {
    if (!firstName || !lastName) return '';
    
    // Generate base slug from first name + last name
    const baseSlug = generateSlug(`${firstName}${lastName}`);
    
    // Check if the slug already exists
    const { data: existingProfiles, error } = await supabase
      .from('profiles')
      .select('slug')
      .ilike('slug', `${baseSlug}%`)
      .neq('user_id', user?.id); // Exclude current profile
    
    if (error) {
      console.error('Error checking slug:', error);
      return baseSlug;
    }
    
    // If it doesn't exist, use base slug
    if (!existingProfiles || existingProfiles.length === 0) {
      return baseSlug;
    }
    
    // If it exists, find the first available number
    let counter = 2;
    let newSlug = `${baseSlug}${counter}`;
    
    while (existingProfiles.some(p => p.slug === newSlug)) {
      counter++;
      newSlug = `${baseSlug}${counter}`;
    }
    
    return newSlug;
  }, [user?.id]);

  const handleNameChange = async (field: 'first_name' | 'last_name', value: string) => {
    const updatedData = { ...profileData, [field]: value };
    setProfileData(updatedData);
    
    // Auto-generate slug only if user hasn't customized it
    // Or if current slug follows the name-pattern
    const currentSlugMatchesName = profileData.slug === generateSlug(
      `${profileData.first_name}${profileData.last_name}`
    );
    
    if (!profileData.slug || currentSlugMatchesName) {
      const newSlug = await generateSlugFromName(
        field === 'first_name' ? value : profileData.first_name,
        field === 'last_name' ? value : profileData.last_name
      );
      setProfileData(prev => ({ ...prev, slug: newSlug }));
    }
  };

  const checkSlugAvailability = useCallback(async (slug: string) => {
    if (!slug || slug === originalSlug) {
      setSlugAvailable(null);
      return;
    }

    setCheckingSlug(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('slug')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      setSlugAvailable(!data);
    } catch (error) {
      console.error('Error checking slug:', error);
      setSlugAvailable(null);
    } finally {
      setCheckingSlug(false);
    }
  }, [originalSlug]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (profileData.slug && profileData.slug !== originalSlug) {
        checkSlugAvailability(profileData.slug);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [profileData.slug, originalSlug, checkSlugAvailability]);

  const handleBusinessNameChange = (value: string) => {
    setProfileData({
      ...profileData,
      business_name: value,
      // No longer auto-generate slug from business_name
    });
  };

  const handleSlugChange = (value: string) => {
    const cleanSlug = generateSlug(value);
    setProfileData({
      ...profileData,
      slug: cleanSlug,
    });
  };

  const handleSave = async () => {
    if (!user) return;
    
    // Check if slug is available before saving
    if (profileData.slug !== originalSlug && slugAvailable === false) {
      toast({
        title: "Errore",
        description: "Lo slug scelto è già in uso. Scegline un altro.",
        variant: "error",
      });
      return;
    }

    // Sanitize ALL input fields before saving
    const sanitizedProfileData = {
      ...profileData,
      first_name: sanitizeName(profileData.first_name || ''),
      last_name: sanitizeName(profileData.last_name || ''),
      business_name: sanitizeText(profileData.business_name || ''),
      bio_it: sanitizeRichText(profileData.bio_it || ''),
      bio_en: sanitizeRichText(profileData.bio_en || ''),
      bio_es: sanitizeRichText(profileData.bio_es || ''),
      description_it: sanitizeRichText(profileData.description_it || ''),
      description_en: sanitizeRichText(profileData.description_en || ''),
      description_es: sanitizeRichText(profileData.description_es || ''),
      phone: sanitizePhone(profileData.phone || ''),
      whatsapp: sanitizePhone(profileData.whatsapp || ''),
      website: sanitizeUrl(profileData.website || ''),
      booking_url: sanitizeUrl(profileData.booking_url || ''),
      video_url: sanitizeVideoUrl(profileData.video_url || ''),
      professional_email: sanitizeEmail(profileData.professional_email || ''),
      social_instagram: sanitizeUrl(profileData.social_instagram || ''),
      social_linkedin: sanitizeUrl(profileData.social_linkedin || ''),
      social_facebook: sanitizeUrl(profileData.social_facebook || ''),
      address_street: sanitizeText(profileData.address_street || ''),
      address_city: sanitizeText(profileData.address_city || ''),
      address_zip: sanitizeText(profileData.address_zip || ''),
      address_region: sanitizeText(profileData.address_region || ''),
      company_tagline: sanitizeText(profileData.company_tagline || ''),
      company_description: sanitizeRichText(profileData.company_description || ''),
      business_type: 'professional' // Always force professional
    };

    // Sanitize data: Remove premium fields if user is not subscribed
    const sanitizedData = { ...sanitizedProfileData };
    
    if (!isSubscriber()) {
      // Remove premium fields
      delete sanitizedData.business_name;
      delete sanitizedData.slug;
      delete sanitizedData.business_type;
      delete sanitizedData.categories;
      delete sanitizedData.bio_it;
      delete sanitizedData.bio_en;
      delete sanitizedData.bio_es;
      delete sanitizedData.phone;
      delete sanitizedData.whatsapp;
      delete sanitizedData.website;
      delete sanitizedData.booking_url;
      delete sanitizedData.social_instagram;
      delete sanitizedData.social_linkedin;
      delete sanitizedData.social_facebook;
      delete sanitizedData.address_street;
      delete sanitizedData.address_city;
      delete sanitizedData.address_zip;
      delete sanitizedData.address_region;
      delete sanitizedData.service_radius_km;
      delete sanitizedData.languages_spoken;
      delete sanitizedData.professional_email;
      delete sanitizedData.price_range;
      delete sanitizedData.online_service;
      delete sanitizedData.in_person_service;
      delete sanitizedData.cover_image;
      delete sanitizedData.video_url;
      delete sanitizedData.team_members;
      delete sanitizedData.company_tagline;
      delete sanitizedData.company_description;
      delete sanitizedData.description_it;
      delete sanitizedData.description_en;
      delete sanitizedData.description_es;
    }

    setSaving(true);

    try {
      // Get existing profile for address comparison
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id, address_street, address_city, address_zip, address_region, address_country')
        .eq('user_id', user.id)
        .maybeSingle();

      // Check if address has changed (for geocoding cache)
      const addressChanged = existingProfile && (
        existingProfile.address_street !== sanitizedData.address_street ||
        existingProfile.address_city !== sanitizedData.address_city ||
        existingProfile.address_zip !== sanitizedData.address_zip ||
        existingProfile.address_region !== sanitizedData.address_region ||
        existingProfile.address_country !== sanitizedData.address_country
      );

      // Save profile data first
      if (existingProfile) {
        const { error } = await supabase
          .from('profiles')
          .update(sanitizedData as any)
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('profiles').insert({
          user_id: user.id,
          ...sanitizedData,
        } as any);
        if (error) throw error;
      }

      // Geocode address ONLY if it changed and is complete
      const hasFullAddress = 
        sanitizedData.address_city &&
        sanitizedData.address_country;
      
      if ((addressChanged || !existingProfile) && hasFullAddress) {
        console.log('💾 Saving address:', {
          street: sanitizedData.address_street,
          city: sanitizedData.address_city,
          country: sanitizedData.address_country,
          hasMapCoords: !!mapCoords,
          addressChanged
        });
        
        // Salva le coordinate del pin se l'utente ha verificato la posizione
        if (mapCoords) {
          await supabase
            .from('profiles')
            .update({
              latitude: mapCoords.lat,
              longitude: mapCoords.lng
            })
            .eq('user_id', user.id);
            
          console.log('✅ Coordinate salvate dalla mappa:', mapCoords);
          
        }
      }

      return true;
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Funzione che salva TUTTI i dati: profilo + azienda (se Plus)
  const handleSaveAll = async () => {
    // Salva prima i dati del profilo
    const profileSaved = await handleSave();
    
    // Traccia se l'utente ha accesso alla Tab Azienda e se il salvataggio è stato tentato/riuscito
    let companySaveAttempted = false;
    let companySaved = false;
    
    // Solo per utenti Plus/Admin che hanno accesso alla Tab Azienda
    if (isPlus()) {
      companySaveAttempted = true;
      if (companyData.name) {
        companySaved = await saveCompanyData();
      }
    }
    
    // Mostra UN SOLO toast con messaggio contestuale
    if (profileSaved) {
      // Invalidate professionals cache so changes appear immediately
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
      
      let toastDescription = '';
      let toastVariant: 'success' | 'warning' = 'success';
      
      if (companySaveAttempted) {
        // Utente Plus/Admin
        if (companySaved) {
          // Ha salvato sia profilo che azienda
          toastDescription = t('profile.savedDescFull');
        } else if (!companyData.name) {
          // Plus ma senza nome azienda inserito
          toastDescription = t('profile.savedDescNoCompanyName');
          toastVariant = 'warning';
        } else {
          // Errore nel salvataggio azienda
          toastDescription = t('profile.savedDescCompanyError');
          toastVariant = 'warning';
        }
      } else {
        // Utente Base/Pro (non ha accesso alla Tab Azienda)
        toastDescription = t('profile.savedDesc');
      }
      
      toast({
        title: t('common.saved'),
        description: toastDescription,
        variant: toastVariant,
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-dark" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        {/* Breadcrumb Navigation */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">
                Dashboard
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t('dashboard.myProfile')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading text-4xl font-bold text-dark">{t('profile.edit.title')}</h1>
        </div>

        {!isSubscriber() && (
          <div className="mb-8 p-6 bg-gradient-to-r from-primary/20 to-primary/10 border-[3px] border-dark shadow-[6px_6px_0px_#101322]">
            <div className="flex items-start gap-4">
              <Lock className="w-8 h-8 text-dark flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-heading text-xl font-bold mb-2 text-dark">
                  {t('profile.edit.banner.unlockPotential')}
                </h3>
                <p className="text-base mb-4 text-dark/80">
                  {t('profile.edit.banner.description')}
                </p>
                <Link to="/pricing">
                  <BrutalButton variant="primary" size="sm">
                    {t('profile.edit.banner.discoverPlans')}
                  </BrutalButton>
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end mb-8">
          <BrutalButton 
            onClick={handleSaveAll} 
            disabled={saving || isSavingCompany || (profileData.slug !== originalSlug && slugAvailable === false)}
            variant="primary"
            size="default"
          >
            {(saving || isSavingCompany) ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {t('common.save')}
          </BrutalButton>
        </div>

      <Tabs defaultValue="basic" className="space-y-8">
        <TabsList className="w-full gap-1">
          <TabsTrigger 
            value="basic"
            className="data-[state=active]:bg-primary data-[state=active]:text-dark data-[state=active]:border-[3px] data-[state=active]:border-dark data-[state=active]:shadow-[4px_4px_0px_#101322] font-heading font-bold text-xs sm:text-sm px-2 py-2"
          >
            {t('profile.edit.tabs.basic')}
          </TabsTrigger>
          <TabsTrigger 
            value="contacts" 
            disabled={!isSubscriber()}
            className="data-[state=active]:bg-primary data-[state=active]:text-dark data-[state=active]:border-[3px] data-[state=active]:border-dark data-[state=active]:shadow-[4px_4px_0px_#101322] font-heading font-bold text-xs sm:text-sm px-2 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('profile.edit.tabs.contacts')} {!isSubscriber() && "🔒"}
          </TabsTrigger>
          <TabsTrigger 
            value="services" 
            disabled={!isSubscriber()}
            className="data-[state=active]:bg-primary data-[state=active]:text-dark data-[state=active]:border-[3px] data-[state=active]:border-dark data-[state=active]:shadow-[4px_4px_0px_#101322] font-heading font-bold text-xs sm:text-sm px-2 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('profile.edit.tabs.services')} {!isSubscriber() && "🔒"}
          </TabsTrigger>
          <TabsTrigger 
            value="media" 
            disabled={!isPro()}
            className="data-[state=active]:bg-primary data-[state=active]:text-dark data-[state=active]:border-[3px] data-[state=active]:border-dark data-[state=active]:shadow-[4px_4px_0px_#101322] font-heading font-bold text-xs sm:text-sm px-2 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('profile.edit.tabs.media')} {!isPro() && "🔒"}
          </TabsTrigger>
          <TabsTrigger 
            value="company" 
            disabled={!isPlus()}
            className="data-[state=active]:bg-primary data-[state=active]:text-dark data-[state=active]:border-[3px] data-[state=active]:border-dark data-[state=active]:shadow-[4px_4px_0px_#101322] font-heading font-bold text-xs sm:text-sm px-2 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('profile.edit.tabs.company')} {!isPlus() && "🔒"}
          </TabsTrigger>
        </TabsList>

          <TabsContent value="basic" className="space-y-6">
            {/* Informazioni Personali */}
            <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
              <h3 className="font-heading text-xl font-bold mb-4">
                {t('profile.edit.sections.personalInfo')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">{t('profile.edit.fields.firstName')}</Label>
                  <BrutalInput
                    id="first_name"
                    value={profileData.first_name}
                    onChange={(e) => handleNameChange('first_name', e.target.value)}
                    placeholder={t('profile.edit.placeholders.firstName')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">{t('profile.edit.fields.lastName')}</Label>
                  <BrutalInput
                    id="last_name"
                    value={profileData.last_name}
                    onChange={(e) => handleNameChange('last_name', e.target.value)}
                    placeholder={t('profile.edit.placeholders.lastName')}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="profile_image" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('profile.edit.fields.profileImage')}
                </label>
                <ImageUpload
                  userId={user?.id || ''}
                  currentImage={profileData.profile_image}
                  onImageUploaded={(url) => setProfileData({ ...profileData, profile_image: url })}
                />
              </div>
            </div>

            {/* Dettagli Professionali */}
            <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4 relative">
              <h3 className="font-heading text-xl font-bold mb-4">
                {t('profile.edit.sections.professionalDetails')}
              </h3>
              
              {!isSubscriber() && (
                <div className="absolute inset-0 bg-card/95 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-6">
                  <Lock className="w-12 h-12 text-primary mb-4" />
                  <h4 className="font-heading font-bold text-xl text-dark mb-2">
                    {t('profile.edit.overlay.premiumRequired')}
                  </h4>
                  <p className="text-dark/70 text-center mb-4 max-w-md">
                    {t('profile.edit.overlay.professionalDetailsDesc')}
                  </p>
                  <Link to="/pricing">
                    <BrutalButton variant="primary" size="sm">
                      {t('profile.edit.overlay.discoverPremium')}
                    </BrutalButton>
                  </Link>
                </div>
              )}
              
              <div className={!isSubscriber() ? "opacity-30 pointer-events-none" : ""}>
                <div className="space-y-2">
                  <Label htmlFor="business_name">{t('profile.edit.fields.businessName')}</Label>
                  <BrutalInput
                    id="business_name"
                    value={profileData.business_name}
                    onChange={(e) => handleBusinessNameChange(e.target.value)}
                    disabled={!isSubscriber()}
                  />
                </div>

                <div className="space-y-2">
                  <CopyableUrlInput
                    id="slug"
                    label={t('profile.edit.fields.urlProfile')}
                    baseUrl="inclusia.pro/professional/"
                    value={profileData.slug}
                    onChange={(value) => handleSlugChange(value)}
                    placeholder="es. mario-rossi-dentista"
                    includeProtocol={true}
                    sanitizeSlug={generateSlug}
                  />
                  
                  {/* Slug Validation Feedback */}
                  <div className="space-y-1">
                    {checkingSlug && (
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('profile.edit.validation.checkingAvailability')}
                      </p>
                    )}
                    
                    {!checkingSlug && profileData.slug !== originalSlug && slugAvailable !== null && (
                      <p className={`text-sm flex items-center gap-2 font-bold font-heading ${
                        slugAvailable ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {slugAvailable ? (
                          <>
                            <CheckCircle2 className="h-4 w-4" />
                            {t('profile.edit.validation.urlAvailable')}
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4" />
                            {t('profile.edit.validation.urlTaken')}
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* Categorie e Specializzazioni */}
            <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4 relative">
              <h3 className="font-heading text-xl font-bold mb-4">
                {t('profile.edit.sections.categoriesSpecializations')}
              </h3>
              
              {!isSubscriber() && (
                <div className="absolute inset-0 bg-card/95 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-6">
                  <Lock className="w-12 h-12 text-primary mb-4" />
                  <h4 className="font-heading font-bold text-xl text-dark mb-2">
                    {t('profile.edit.overlay.premiumRequired')}
                  </h4>
                  <p className="text-dark/70 text-center mb-4 max-w-md">
                    {t('profile.edit.overlay.categoriesDesc')}
                  </p>
                  <Link to="/pricing">
                    <BrutalButton variant="primary" size="sm">
                      {t('profile.edit.overlay.discoverPremium')}
                    </BrutalButton>
                  </Link>
                </div>
              )}
              
              <div className={!isSubscriber() ? "opacity-30 pointer-events-none" : ""}>
                <div className="space-y-2">
                  <Label>
                    {t('profile.edit.fields.categoriesWithCount', { 
                      count: profileData.categories.length, 
                      max: 3 
                    })}
                  </Label>
                  <CategorySelector
                    selectedCategories={profileData.categories}
                    onChange={(categories) => setProfileData({ ...profileData, categories })}
                    maxCategories={3}
                    disabled={!isSubscriber()}
                  />
                </div>
              </div>
            </div>

            {/* Biografia Multilingua */}
            <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4 relative">
              <h3 className="font-heading text-xl font-bold mb-4">
                {t('profile.edit.sections.multilingualBio')}
                {!isPro() && <span className="text-sm font-normal text-muted-foreground ml-2">({t('profile.edit.overlay.yourLanguageOnly')})</span>}
              </h3>
              
              <Tabs defaultValue={userPreferredLang} className="w-full">
                <TabsList className="mb-4 border-[2px] border-dark bg-background">
                  <TabsTrigger 
                    value="it" 
                    disabled={!isPro() && userPreferredLang !== 'it'}
                    className="gap-2"
                  >
                    🇮🇹 <span className="hidden sm:inline">Italiano</span>
                    {profileData.bio_it && <span className="text-xs text-primary">✓</span>}
                    {!isPro() && userPreferredLang !== 'it' && " 🔒"}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="en" 
                    disabled={!isPro() && userPreferredLang !== 'en'}
                    className="gap-2"
                  >
                    🇬🇧 <span className="hidden sm:inline">English</span>
                    {profileData.bio_en && <span className="text-xs text-primary">✓</span>}
                    {!isPro() && userPreferredLang !== 'en' && " 🔒"}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="es" 
                    disabled={!isPro() && userPreferredLang !== 'es'}
                    className="gap-2"
                  >
                    🇪🇸 <span className="hidden sm:inline">Español</span>
                    {profileData.bio_es && <span className="text-xs text-primary">✓</span>}
                    {!isPro() && userPreferredLang !== 'es' && " 🔒"}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="it">
                  {isPro() || userPreferredLang === 'it' ? (
                    <SimpleRichTextEditor
                      value={profileData.bio_it}
                      onChange={(html) => setProfileData({ ...profileData, bio_it: html })}
                      maxLength={500}
                    />
                  ) : (
                    <div className="p-6 text-center bg-primary/10 border-[2px] border-dark">
                      <Lock className="w-8 h-8 mx-auto mb-3 text-primary" />
                      <p className="font-bold mb-2">{t('profile.edit.overlay.multilingualBioPro')}</p>
                      <p className="text-sm text-muted-foreground mb-4">{t('profile.edit.overlay.multilingualBioDesc')}</p>
                      <Link to="/pricing">
                        <BrutalButton variant="primary" size="sm">
                          {t('profile.edit.overlay.upgradeToPro')}
                        </BrutalButton>
                      </Link>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="en">
                  {isPro() || userPreferredLang === 'en' ? (
                    <SimpleRichTextEditor
                      value={profileData.bio_en}
                      onChange={(html) => setProfileData({ ...profileData, bio_en: html })}
                      maxLength={500}
                    />
                  ) : (
                    <div className="p-6 text-center bg-primary/10 border-[2px] border-dark">
                      <Lock className="w-8 h-8 mx-auto mb-3 text-primary" />
                      <p className="font-bold mb-2">{t('profile.edit.overlay.multilingualBioPro')}</p>
                      <p className="text-sm text-muted-foreground mb-4">{t('profile.edit.overlay.multilingualBioDesc')}</p>
                      <Link to="/pricing">
                        <BrutalButton variant="primary" size="sm">
                          {t('profile.edit.overlay.upgradeToPro')}
                        </BrutalButton>
                      </Link>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="es">
                  {isPro() || userPreferredLang === 'es' ? (
                    <SimpleRichTextEditor
                      value={profileData.bio_es}
                      onChange={(html) => setProfileData({ ...profileData, bio_es: html })}
                      maxLength={500}
                    />
                  ) : (
                    <div className="p-6 text-center bg-primary/10 border-[2px] border-dark">
                      <Lock className="w-8 h-8 mx-auto mb-3 text-primary" />
                      <p className="font-bold mb-2">{t('profile.edit.overlay.multilingualBioPro')}</p>
                      <p className="text-sm text-muted-foreground mb-4">{t('profile.edit.overlay.multilingualBioDesc')}</p>
                      <Link to="/pricing">
                        <BrutalButton variant="primary" size="sm">
                          {t('profile.edit.overlay.upgradeToPro')}
                        </BrutalButton>
                      </Link>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="space-y-4">
            {!isSubscriber() ? (
              <div className="p-10 text-center bg-card border-[3px] border-dark shadow-[8px_8px_0px_#101322]">
                <Lock className="w-16 h-16 mx-auto mb-6 text-primary" />
                <h3 className="font-heading text-2xl font-bold mb-3 text-dark">
                  🔒 {t('profile.edit.upgrade.title')}
                </h3>
                <p className="mb-4 text-dark/70 text-base">
                  {t('profile.edit.upgrade.contactsLocked')}
                </p>
                <div className="mb-6 p-4 bg-primary/10 border-[2px] border-dark inline-block">
                  <p className="text-sm font-bold text-dark">{t('profile.edit.features.contacts')}</p>
                </div>
                <Link to="/pricing">
                  <BrutalButton variant="primary" size="default">
                    Scopri i vantaggi Premium →
                  </BrutalButton>
                </Link>
              </div>
            ) : (
              <>
                <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                  <h3 className="font-heading text-xl font-bold mb-4">
                    {t('profile.edit.sections.contactsBasic')}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t('profile.edit.fields.phone')}</Label>
                      <BrutalInput
                        id="phone"
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        placeholder={t('profile.edit.placeholders.phone')}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="professional_email">{t('profile.edit.fields.professionalEmail')}</Label>
                      <BrutalInput
                        id="professional_email"
                        type="email"
                        value={profileData.professional_email}
                        onChange={(e) => setProfileData({ ...profileData, professional_email: e.target.value })}
                        placeholder="contatto@esempio.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                  <h3 className="font-heading text-xl font-bold mb-4">
                    {t('profile.edit.sections.contactsAdvanced')}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp">{t('profile.edit.fields.whatsapp')}</Label>
                      <BrutalInput
                        id="whatsapp"
                        type="tel"
                        value={profileData.whatsapp}
                        onChange={(e) => setProfileData({ ...profileData, whatsapp: e.target.value })}
                        placeholder={t('profile.edit.placeholders.whatsapp')}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="website">{t('profile.edit.fields.website')}</Label>
                      <BrutalInput
                        id="website"
                        type="url"
                        value={profileData.website}
                        onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
                        placeholder={t('profile.edit.placeholders.website')}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="booking_url">{t('profile.edit.fields.bookingUrl')}</Label>
                      <BrutalInput
                        id="booking_url"
                        type="url"
                        value={profileData.booking_url}
                        onChange={(e) => setProfileData({ ...profileData, booking_url: e.target.value })}
                        placeholder={t('profile.edit.placeholders.bookingUrl')}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                  <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                    {t('profile.edit.sections.socialMedia')}
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="instagram" className="flex items-center gap-2">
                        <Instagram className="w-4 h-4" />
                        {t('profile.edit.fields.instagram')}
                      </Label>
                      <BrutalInput
                        id="instagram"
                        value={profileData.social_instagram}
                        onChange={(e) => setProfileData({ ...profileData, social_instagram: e.target.value })}
                        placeholder={t('profile.edit.placeholders.instagram')}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="linkedin" className="flex items-center gap-2">
                        <Linkedin className="w-4 h-4" />
                        {t('profile.edit.fields.linkedin')}
                      </Label>
                      <BrutalInput
                        id="linkedin"
                        value={profileData.social_linkedin}
                        onChange={(e) => setProfileData({ ...profileData, social_linkedin: e.target.value })}
                        placeholder={t('profile.edit.placeholders.linkedin')}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="facebook" className="flex items-center gap-2">
                        <Facebook className="w-4 h-4" />
                        {t('profile.edit.fields.facebook')}
                      </Label>
                      <BrutalInput
                        id="facebook"
                        value={profileData.social_facebook}
                        onChange={(e) => setProfileData({ ...profileData, social_facebook: e.target.value })}
                        placeholder={t('profile.edit.placeholders.facebook')}
                      />
                    </div>
                  </div>
                </div>

                {/* Localizzazione e Copertura */}
                <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                  <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    {t('profile.edit.sections.location')}
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="address_street">{t('profile.edit.fields.street')}</Label>
                      <BrutalInput
                        id="address_street"
                        value={profileData.address_street}
                        onChange={(e) => setProfileData({ ...profileData, address_street: e.target.value })}
                        placeholder="Via Roma, 123"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address_city">{t('profile.edit.fields.city')}</Label>
                      <BrutalInput
                        id="address_city"
                        value={profileData.address_city}
                        onChange={(e) => setProfileData({ ...profileData, address_city: e.target.value })}
                        placeholder="Milano"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address_zip">{t('profile.edit.fields.zip')}</Label>
                      <BrutalInput
                        id="address_zip"
                        value={profileData.address_zip}
                        onChange={(e) => setProfileData({ ...profileData, address_zip: e.target.value })}
                        placeholder="20100"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address_region">{t('profile.edit.fields.companyRegion')}</Label>
                      <BrutalInput
                        id="address_region"
                        value={profileData.address_region}
                        onChange={(e) => setProfileData({ ...profileData, address_region: e.target.value })}
                        placeholder="Lombardia"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address_country">{t('profile.edit.fields.country')}</Label>
                      <BrutalInput
                        id="address_country"
                        value={profileData.address_country}
                        onChange={(e) => setProfileData({ ...profileData, address_country: e.target.value })}
                        placeholder="Italia"
                      />
                    </div>
                  </div>

                  {/* Verifica Posizione */}
                  <div className="pt-4 border-t-2 border-dashed border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {t('profile.addressVerification')}
                        </span>
                      </div>
                      <BrutalButton
                        onClick={handleVerifyAddress}
                        disabled={geocodingStatus === 'loading' || !profileData.address_city || !profileData.address_country}
                        size="sm"
                        variant={geocodingStatus === 'success' ? 'primary' : 'outline'}
                      >
                        {geocodingStatus === 'loading' ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('profile.geocodingInProgress')}
                          </>
                        ) : geocodingStatus === 'success' ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                            Indirizzo Verificato
                          </>
                        ) : geocodingStatus === 'error' ? (
                          <>
                            <XCircle className="w-4 h-4 mr-2 text-red-600" />
                            Riprova Verifica
                          </>
                        ) : (
                          <>
                            <MapPin className="w-4 h-4 mr-2" />
                            {t('profile.verifyAddress')}
                          </>
                        )}
                      </BrutalButton>
                    </div>

                    {/* Status indicator */}
                    {geocodingStatus !== 'idle' && (
                      <div className={`p-3 mb-3 border-[3px] border-dark text-sm ${
                        geocodingStatus === 'success' ? 'bg-green-50' :
                        geocodingStatus === 'error' ? 'bg-red-50' :
                        'bg-blue-50'
                      }`}>
                        {geocodingStatus === 'loading' && (
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>{t('profile.geocodingInProgress')}</span>
                          </div>
                        )}
                        {geocodingStatus === 'success' && mapCoords && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 font-medium text-green-800">
                              <CheckCircle2 className="w-4 h-4" />
                              ✅ Città localizzata
                            </div>
                            <p className="text-sm text-muted-foreground">
                              📍 Trascina il pin sulla mappa per posizionarlo esattamente
                            </p>
                            <div className="text-xs font-mono text-muted-foreground">
                              {mapCoords.lat.toFixed(6)}, {mapCoords.lng.toFixed(6)}
                            </div>
                          </div>
                        )}
                        {geocodingStatus === 'error' && (
                          <div className="flex items-center gap-2 text-red-800">
                            <XCircle className="w-4 h-4" />
                            {t('profile.geocodingError')}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Mappa con pin draggable */}
                    {mapCoords ? (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          📍 Trascina il pin per posizionarlo esattamente
                        </Label>
                        <AddressPreviewMap 
                          latitude={mapCoords.lat}
                          longitude={mapCoords.lng}
                          displayName={`${profileData.address_city}, ${profileData.address_country}`}
                          onPositionChange={handleMapPositionChange}
                        />
                        <p className="text-xs text-muted-foreground font-mono">
                          Coordinate: {mapCoords.lat.toFixed(6)}, {mapCoords.lng.toFixed(6)}
                        </p>
                      </div>
                    ) : (
                      <div className="w-full h-[200px] bg-muted border-[3px] border-dark flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Clicca "🔍 Verifica Posizione" per vedere la mappa</p>
                        </div>
                      </div>
                    )}

                    {/* Tooltip campi obbligatori */}
                    {(!profileData.address_city || !profileData.address_country) && (
                      <p className="text-xs text-muted-foreground mt-2">
                        💡 {t('profile.requiredForMap')}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service_radius">{t('profile.edit.fields.serviceRadius')}</Label>
                    <BrutalInput
                      id="service_radius"
                      type="number"
                      value={profileData.service_radius_km}
                      onChange={(e) =>
                        setProfileData({ ...profileData, service_radius_km: parseInt(e.target.value) || 0 })
                      }
                      placeholder="50"
                    />
                    <p className="text-xs text-muted-foreground">{t('profile.edit.fields.serviceRadiusDesc')}</p>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="services" className="space-y-4">
            {!isSubscriber() ? (
              <div className="p-10 text-center bg-card border-[3px] border-dark shadow-[8px_8px_0px_#101322]">
                <Lock className="w-16 h-16 mx-auto mb-6 text-primary" />
                <h3 className="font-heading text-2xl font-bold mb-3 text-dark">
                  🔒 {t('profile.edit.upgrade.title')}
                </h3>
                <p className="mb-4 text-dark/70 text-base">
                  {t('profile.edit.upgrade.servicesLocked')}
                </p>
                <div className="mb-6 p-4 bg-primary/10 border-[2px] border-dark inline-block">
                  <p className="text-sm font-bold text-dark">{t('profile.edit.upgrade.servicesFeatures')}</p>
                </div>
                <Link to="/pricing">
                  <BrutalButton variant="primary" size="default">
                    {t('profile.edit.upgrade.discoverPremium')} →
                  </BrutalButton>
                </Link>
              </div>
            ) : (
              <>
                <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                  <h3 className="font-heading text-xl font-bold mb-4">
                    {t('profile.edit.fields.priceRange')}
                  </h3>
                  <BrutalSelect
                    value={profileData.price_range}
                    onValueChange={(value) => setProfileData({ ...profileData, price_range: value })}
                  >
                    <BrutalSelectTrigger>
                      <BrutalSelectValue placeholder={t('profile.edit.priceRanges.select')} />
                    </BrutalSelectTrigger>
                    <BrutalSelectContent>
                      <BrutalSelectItem value="€">{t('profile.edit.priceRanges.economical')}</BrutalSelectItem>
                      <BrutalSelectItem value="€€">{t('profile.edit.priceRanges.medium')}</BrutalSelectItem>
                      <BrutalSelectItem value="€€€">{t('profile.edit.priceRanges.premium')}</BrutalSelectItem>
                      <BrutalSelectItem value="€€€€">{t('profile.edit.priceRanges.luxury')}</BrutalSelectItem>
                    </BrutalSelectContent>
                  </BrutalSelect>
                </div>

                <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                  <h3 className="font-heading text-xl font-bold mb-4">
                    {t('profile.edit.fields.languages')}
                  </h3>
                  <BrutalMultiSelect
                    options={getLanguageOptions()}
                    selected={profileData.languages_spoken || []}
                    onChange={(languages) => setProfileData({ ...profileData, languages_spoken: languages })}
                    placeholder={t('profile.edit.fields.languagesPlaceholder')}
                    searchPlaceholder={t('profile.edit.fields.languagesSearchPlaceholder')}
                    emptyMessage={t('profile.edit.fields.noLanguagesFound')}
                  />
                </div>

                <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                  <h3 className="font-heading text-xl font-bold mb-4">
                    {t('profile.edit.serviceTypes.title')}
                  </h3>
                  <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={profileData.in_person_service}
                        onChange={(e) => setProfileData({ ...profileData, in_person_service: e.target.checked })}
                        className="w-5 h-5 border-[2px] border-dark accent-primary"
                      />
                      <span className="font-body text-base font-bold">
                        {t('profile.edit.fields.inPersonService')}
                      </span>
                    </label>
                    
                    {profileData.in_person_service && (
                      <div className="ml-8 space-y-2">
                        <Label htmlFor="service_radius_km">
                          {t('profile.edit.fields.serviceRadius')}
                        </Label>
                        <div className="flex items-center gap-2">
                          <BrutalInput
                            id="service_radius_km"
                            type="number"
                            value={profileData.service_radius_km}
                            onChange={(e) => setProfileData({ ...profileData, service_radius_km: parseInt(e.target.value) || 0 })}
                            min="0"
                            max="200"
                            className="w-32"
                          />
                          <span className="font-body">km</span>
                        </div>
                      </div>
                    )}
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={profileData.online_service}
                        onChange={(e) => setProfileData({ ...profileData, online_service: e.target.checked })}
                        className="w-5 h-5 border-[2px] border-dark accent-primary"
                      />
                      <span className="font-body text-base font-bold">
                        {t('profile.edit.fields.onlineService')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                  <h3 className="font-heading text-xl font-bold mb-4">
                    {t('profile.edit.sections.detailedDescription')}
                  </h3>
                  <Tabs defaultValue="it" className="w-full">
                    <TabsList className="mb-4 border-[2px] border-dark bg-background">
                      <TabsTrigger value="it" className="gap-2">
                        🇮🇹 <span className="hidden sm:inline">Italiano</span>
                        {profileData.description_it && <span className="text-xs text-primary">✓</span>}
                      </TabsTrigger>
                      <TabsTrigger value="en" className="gap-2">
                        🇬🇧 <span className="hidden sm:inline">English</span>
                        {profileData.description_en && <span className="text-xs text-primary">✓</span>}
                      </TabsTrigger>
                      <TabsTrigger value="es" className="gap-2">
                        🇪🇸 <span className="hidden sm:inline">Español</span>
                        {profileData.description_es && <span className="text-xs text-primary">✓</span>}
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="it">
                      <SimpleRichTextEditor
                        value={profileData.description_it}
                        onChange={(html) => setProfileData({ ...profileData, description_it: html })}
                        maxLength={2000}
                      />
                    </TabsContent>

                    <TabsContent value="en">
                      <SimpleRichTextEditor
                        value={profileData.description_en}
                        onChange={(html) => setProfileData({ ...profileData, description_en: html })}
                        maxLength={2000}
                      />
                    </TabsContent>

                    <TabsContent value="es">
                      <SimpleRichTextEditor
                        value={profileData.description_es}
                        onChange={(html) => setProfileData({ ...profileData, description_es: html })}
                        maxLength={2000}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="media" className="space-y-4">
            {!isPro() ? (
              <div className="p-10 text-center bg-card border-[3px] border-dark shadow-[8px_8px_0px_#101322]">
                <Lock className="w-16 h-16 mx-auto mb-6 text-primary" />
                <h3 className="font-heading text-2xl font-bold mb-3 text-dark">
                  🔒 {t('profile.edit.upgrade.title')}
                </h3>
                <p className="mb-4 text-dark/70 text-base">
                  {t('profile.edit.upgrade.mediaLockedPro')}
                </p>
                <div className="mb-6 p-4 bg-primary/10 border-[2px] border-dark inline-block">
                  <p className="text-sm font-bold text-dark">{t('profile.edit.features.media')}</p>
                </div>
                <Link to="/pricing">
                  <BrutalButton variant="primary" size="default">
                    {t('profile.edit.overlay.upgradeToPro')}
                  </BrutalButton>
                </Link>
              </div>
            ) : (
              <>
                <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-6">
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-4">
                      {t('profile.edit.fields.coverImage')}
                    </h3>
                    <div className="space-y-2">
                      <Label htmlFor="cover_image">{t('profile.edit.fields.coverImage')}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t('profile.edit.fields.coverImageDesc')}
                      </p>
                      <CoverImageUpload
                        userId={user?.id || ''}
                        currentImage={profileData.cover_image}
                        onImageUploaded={(url) => setProfileData({ ...profileData, cover_image: url })}
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold mb-4">
                      {t('profile.edit.fields.videoUrl')}
                    </h3>
                    <BrutalInput
                      type="url"
                      value={profileData.video_url}
                      onChange={(e) => setProfileData({ ...profileData, video_url: e.target.value })}
                      placeholder={t('profile.edit.placeholders.videoUrl')}
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      Incolla il link di un video YouTube o Vimeo
                    </p>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* TAB: Company */}
          <TabsContent value="company">
            <div className="space-y-6">
              {isPlus() ? (
                <div className="space-y-6">
                  {/* Company Info Section */}
                  <section className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                    <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                      <Building className="w-6 h-6" />
                      {t('profile.edit.sections.companyInfo')}
                    </h3>
                    
                    <div className="space-y-6">
                      {/* Nome e URL */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="company-name">{t('profile.edit.fields.companyName')} *</Label>
                          <BrutalInput
                            id="company-name"
                            value={companyData.name}
                            onChange={(e) => handleCompanyNameChange(e.target.value)}
                            placeholder={t('profile.edit.placeholders.companyName')}
                            required
                          />
                        </div>
                        
              <CopyableUrlInput
                id="company-slug"
                label={t('profile.edit.fields.companySlug')}
                baseUrl="inclusia.pro/azienda/"
                value={companyData.slug}
                onChange={(slug) => setCompanyData({...companyData, slug})}
                placeholder={t('profile.edit.placeholders.companySlug')}
                sanitizeSlug={(slug) => slug.toLowerCase().replace(/[^a-z0-9-]/g, '')}
                includeProtocol={true}
              />
                      </div>
                      
                      {/* Categorie azienda */}
                      <div className="space-y-2">
                        <Label>{t('profile.edit.fields.companyCategories')}</Label>
                        <CategorySelector
                          selectedCategories={companyData.categories}
                          onChange={(categories) => setCompanyData({...companyData, categories})}
                          maxCategories={3}
                        />
                      </div>
                      
                      {/* Tagline multilingua */}
                      <div className="space-y-2">
                        <Label>{t('profile.edit.fields.companyTagline')}</Label>
                        <div className="space-y-3 mt-2">
                          <div className="space-y-2">
                            <Label className="text-sm block mb-1">🇮🇹 Italiano</Label>
                            <BrutalInput
                              value={companyData.tagline_it}
                              onChange={(e) => setCompanyData({...companyData, tagline_it: e.target.value})}
                              maxLength={150}
                              placeholder={t('profile.edit.placeholders.taglineIt')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm block mb-1">🇬🇧 English</Label>
                            <BrutalInput
                              value={companyData.tagline_en}
                              onChange={(e) => setCompanyData({...companyData, tagline_en: e.target.value})}
                              maxLength={150}
                              placeholder={t('profile.edit.placeholders.taglineEn')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm block mb-1">🇪🇸 Español</Label>
                            <BrutalInput
                              value={companyData.tagline_es}
                              onChange={(e) => setCompanyData({...companyData, tagline_es: e.target.value})}
                              maxLength={150}
                              placeholder={t('profile.edit.placeholders.taglineEs')}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Descrizione multilingua */}
                      <div className="space-y-2">
                        <Label>{t('profile.edit.fields.companyDescription')}</Label>
                        <Tabs defaultValue="it" className="w-full mt-2">
                          <TabsList className="mb-4 border-[2px] border-dark bg-background">
                            <TabsTrigger value="it" className="gap-2">
                              🇮🇹 <span className="hidden sm:inline">Italiano</span>
                              {companyData.description_it && <span className="text-xs text-primary">✓</span>}
                            </TabsTrigger>
                            <TabsTrigger value="en" className="gap-2">
                              🇬🇧 <span className="hidden sm:inline">English</span>
                              {companyData.description_en && <span className="text-xs text-primary">✓</span>}
                            </TabsTrigger>
                            <TabsTrigger value="es" className="gap-2">
                              🇪🇸 <span className="hidden sm:inline">Español</span>
                              {companyData.description_es && <span className="text-xs text-primary">✓</span>}
                            </TabsTrigger>
                          </TabsList>

                          <TabsContent value="it">
                            <SimpleRichTextEditor
                              value={companyData.description_it}
                              onChange={(html) => setCompanyData({...companyData, description_it: html})}
                              placeholder={t('profile.edit.placeholders.descriptionIt')}
                            />
                          </TabsContent>

                          <TabsContent value="en">
                            <SimpleRichTextEditor
                              value={companyData.description_en}
                              onChange={(html) => setCompanyData({...companyData, description_en: html})}
                              placeholder={t('profile.edit.placeholders.descriptionEn')}
                            />
                          </TabsContent>

                          <TabsContent value="es">
                            <SimpleRichTextEditor
                              value={companyData.description_es}
                              onChange={(html) => setCompanyData({...companyData, description_es: html})}
                              placeholder={t('profile.edit.placeholders.descriptionEs')}
                            />
                          </TabsContent>
                        </Tabs>
                      </div>
                      
                      {/* Logo */}
                      <div className="space-y-2">
                        <Label>{t('profile.edit.fields.companyLogo')}</Label>
                        <div className="flex items-center gap-4 mt-2">
                          <div className="w-24 h-24 border-[3px] border-dark bg-muted flex items-center justify-center overflow-hidden">
                            {companyData.logo_url ? (
                              <img src={companyData.logo_url} alt="Logo" className="w-full h-full object-contain p-2" loading="lazy" />
                            ) : (
                              <Camera className="w-8 h-8 text-muted-foreground" />
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <input
                              ref={companyLogoInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/svg+xml,image/webp"
                              onChange={handleCompanyLogoUpload}
                              className="hidden"
                              id="company-logo-upload"
                            />
                            
                            <BrutalButton 
                              variant="outline" 
                              size="sm"
                              onClick={() => companyLogoInputRef.current?.click()}
                              disabled={uploadingCompanyLogo}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              {uploadingCompanyLogo ? "Caricamento..." : t('profile.edit.company.uploadLogo')}
                            </BrutalButton>
                            
                            {companyData.logo_url && (
                              <BrutalButton 
                                variant="danger" 
                                size="sm"
                                onClick={handleRemoveCompanyLogo}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Rimuovi
                              </BrutalButton>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Formati: JPG, PNG, SVG, WebP • Max 2MB
                        </p>
                      </div>
                      
                      {/* Cover Image */}
                      <div className="space-y-2">
                        <Label>{t('profile.edit.fields.coverImage', 'Immagine di Copertina')}</Label>
                        <CoverImageUpload
                          userId={user?.id || ''}
                          currentImage={companyData.cover_image_url}
                          onImageUploaded={(url) => setCompanyData({...companyData, cover_image_url: url})}
                        />
                      </div>
                      
                      {/* Video YouTube */}
                      <div className="space-y-2">
                        <Label htmlFor="company-video">{t('profile.edit.fields.videoUrl', 'Video di Presentazione (YouTube)')}</Label>
                        <BrutalInput
                          id="company-video"
                          type="url"
                          value={companyData.video_url}
                          onChange={(e) => setCompanyData({...companyData, video_url: e.target.value})}
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                        <p className="text-xs text-muted-foreground">
                          Inserisci l'URL del video YouTube per mostrarlo nella pagina aziendale
                        </p>
                      </div>
                      
                      {/* Lingue Parlate */}
                      <div className="space-y-2">
                        <Label>{t('profile.edit.fields.languagesSpoken', 'Lingue Parlate')}</Label>
                        <BrutalMultiSelect
                          options={getLanguageOptions()}
                          selected={companyData.languages_spoken}
                          onChange={(values) => setCompanyData({...companyData, languages_spoken: values})}
                          placeholder={t('profile.edit.placeholders.selectLanguages', 'Seleziona lingue...')}
                          searchPlaceholder={t('common.search', 'Cerca...')}
                          emptyMessage={t('common.noResults', 'Nessun risultato')}
                        />
                        <p className="text-xs text-muted-foreground">
                          Le lingue parlate dalla tua realtà aziendale
                        </p>
                      </div>
                      
                      {/* Contatti */}
                      <div className="space-y-2">
                        <Label className="mb-2 block">{t('profile.edit.sections.contacts')}</Label>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="company-email">{t('profile.edit.fields.companyEmail')}</Label>
                            <BrutalInput
                              id="company-email"
                              type="email"
                              value={companyData.email}
                              onChange={(e) => setCompanyData({...companyData, email: e.target.value})}
                              placeholder="info@azienda.it"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="company-phone">{t('profile.edit.fields.companyPhone')}</Label>
                            <BrutalInput
                              id="company-phone"
                              type="tel"
                              value={companyData.phone}
                              onChange={(e) => setCompanyData({...companyData, phone: e.target.value})}
                              placeholder="+39 02 1234567"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="company-whatsapp">{t('profile.edit.fields.companyWhatsapp')}</Label>
                            <BrutalInput
                              id="company-whatsapp"
                              type="tel"
                              value={companyData.whatsapp}
                              onChange={(e) => setCompanyData({...companyData, whatsapp: e.target.value})}
                              placeholder="+39 333 1234567"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="company-website">{t('profile.edit.fields.companyWebsite')}</Label>
                            <BrutalInput
                              id="company-website"
                              type="url"
                              value={companyData.website}
                              onChange={(e) => setCompanyData({...companyData, website: e.target.value})}
                              placeholder="https://azienda.it"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Social Media */}
                      <div className="space-y-2">
                        <Label className="mb-2 block">Social Media</Label>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="company-instagram" className="flex items-center gap-2">
                              <Instagram className="w-4 h-4" />
                              Instagram
                            </Label>
                            <BrutalInput
                              id="company-instagram"
                              value={companyData.social_instagram}
                              onChange={(e) => setCompanyData({...companyData, social_instagram: e.target.value})}
                              placeholder="@azienda"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="company-linkedin" className="flex items-center gap-2">
                              <Linkedin className="w-4 h-4" />
                              LinkedIn
                            </Label>
                            <BrutalInput
                              id="company-linkedin"
                              value={companyData.social_linkedin}
                              onChange={(e) => setCompanyData({...companyData, social_linkedin: e.target.value})}
                              placeholder="linkedin.com/company/azienda"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="company-facebook" className="flex items-center gap-2">
                              <Facebook className="w-4 h-4" />
                              Facebook
                            </Label>
                            <BrutalInput
                              id="company-facebook"
                              value={companyData.social_facebook}
                              onChange={(e) => setCompanyData({...companyData, social_facebook: e.target.value})}
                              placeholder="facebook.com/azienda"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Indirizzo e P.IVA */}
                      <div className="space-y-2">
                        <Label className="mb-2 block flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          Sede e Dati Fiscali
                        </Label>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="company-street">Via/Piazza</Label>
                            <BrutalInput
                              id="company-street"
                              value={companyData.address_street}
                              onChange={(e) => setCompanyData({...companyData, address_street: e.target.value})}
                              placeholder="Via/Piazza"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company-city">{t('profile.edit.fields.companyCity')}</Label>
                            <BrutalInput
                              id="company-city"
                              value={companyData.address_city}
                              onChange={(e) => setCompanyData({...companyData, address_city: e.target.value})}
                              placeholder={t('profile.edit.fields.companyCity')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company-zip">{t('profile.edit.fields.companyZip')}</Label>
                            <BrutalInput
                              id="company-zip"
                              value={companyData.address_zip}
                              onChange={(e) => setCompanyData({...companyData, address_zip: e.target.value})}
                              placeholder={t('profile.edit.fields.companyZip')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company-region">{t('profile.edit.fields.companyRegion')}</Label>
                            <BrutalInput
                              id="company-region"
                              value={companyData.address_region}
                              onChange={(e) => setCompanyData({...companyData, address_region: e.target.value})}
                              placeholder={t('profile.edit.fields.companyRegion')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company-country">{t('profile.edit.fields.country')}</Label>
                            <BrutalInput
                              id="company-country"
                              value={companyData.address_country}
                              onChange={(e) => setCompanyData({...companyData, address_country: e.target.value})}
                              placeholder={t('profile.edit.placeholders.country')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company-vat">{t('profile.edit.fields.companyVat')}</Label>
                            <BrutalInput
                              id="company-vat"
                              value={companyData.vat_number}
                              onChange={(e) => setCompanyData({...companyData, vat_number: e.target.value})}
                              placeholder={t('profile.edit.fields.companyVat')}
                            />
                          </div>
                        </div>
                        
                        {/* Bottone Verifica Indirizzo e Mappa */}
                        <div className="mt-4 space-y-4">
                          <BrutalButton
                            type="button"
                            variant="outline"
                            onClick={handleVerifyCompanyAddress}
                            disabled={companyGeocodingStatus === 'loading' || !companyData.address_city}
                            className="w-full md:w-auto"
                          >
                            {companyGeocodingStatus === 'loading' ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Verifica in corso...
                              </>
                            ) : companyGeocodingStatus === 'success' ? (
                              <>
                                <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                                Indirizzo Verificato
                              </>
                            ) : companyGeocodingStatus === 'error' ? (
                              <>
                                <XCircle className="w-4 h-4 mr-2 text-red-600" />
                                Riprova Verifica
                              </>
                            ) : (
                              <>
                                <MapPin className="w-4 h-4 mr-2" />
                                Verifica Indirizzo
                              </>
                            )}
                          </BrutalButton>
                          
                          {/* Mappa Anteprima */}
                          {(companyGeocodingStatus === 'success' || companyMapCoords) && (
                            <div className="space-y-2">
                              <Label className="text-sm text-muted-foreground">
                                📍 Trascina il pin per posizionarlo esattamente sulla sede
                              </Label>
                              <AddressPreviewMap
                                latitude={companyMapCoords?.lat}
                                longitude={companyMapCoords?.lng}
                                displayName={companyData.name}
                                onPositionChange={handleCompanyMapPositionChange}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  </section>

                  {/* Team Management Section */}
                  <section className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 space-y-4">
                    <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                      <Users className="w-6 h-6" />
                      {t('profile.edit.sections.companyTeam')}
                    </h3>
                    
                    {!companyData.name && (
                      <div className="mb-4 p-4 border-[2px] border-warning bg-warning/10 rounded">
                        <p className="text-sm text-warning-foreground">
                          ℹ️ {t('profile.edit.fields.companyNameRequired')}
                        </p>
                      </div>
                    )}
                    
                    <div className="mb-4 flex justify-between items-center">
                      <p className="text-muted-foreground">
                        Membri del team ({teamMembers.length}/10)
                      </p>
                      {teamMembers.length < 10 && (
                        <BrutalButton 
                          onClick={() => setShowAddTeamModal(true)}
                          variant="primary"
                          disabled={!companyData.name}
                          title={!companyData.name ? t('profile.edit.fields.fillCompanyNameFirst') : ''}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {t('profile.edit.fields.addTeamMember')}
                        </BrutalButton>
                      )}
                      </div>
                      
                      {teamMembers.length > 0 ? (
                        <div className="grid md:grid-cols-2 gap-4">
                          {teamMembers.map((member: any, index: number) => (
                            <div 
                              key={index} 
                              className="border-[3px] border-dark p-4 bg-card hover:shadow-[4px_4px_0px_#101322] transition-all"
                            >
                              <div className="flex gap-4">
                                <div className="w-20 h-20 border-[2px] border-dark bg-muted flex-shrink-0">
                                  {member.photo_url ? (
                                    <img 
                                      src={member.photo_url} 
                                      alt={member.name} 
                                      className="w-full h-full object-cover" 
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <User className="w-8 h-8 text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-bold text-lg truncate">{member.name}</h4>
                                  <p className="text-primary font-semibold text-sm">
                                    {member[`role_${t('code')}`] || member.role_it || ''}
                                  </p>
                                  {(member[`bio_${t('code')}`] || member.bio_it) && (
                                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                      {member[`bio_${t('code')}`] || member.bio_it}
                                    </p>
                                  )}
                                </div>
                                
                                <div className="flex flex-col gap-1 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => handleEditTeamMember(index)}
                                    className="p-1 hover:bg-muted rounded transition-colors"
                                    title={t('profile.edit.fields.editTeamMember')}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleMoveTeamMember(index, 'up')}
                                    disabled={index === 0}
                                    className="p-1 hover:bg-muted rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    title={t('profile.edit.fields.moveUp')}
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleMoveTeamMember(index, 'down')}
                                    disabled={index === teamMembers.length - 1}
                                    className="p-1 hover:bg-muted rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    title={t('profile.edit.fields.moveDown')}
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveTeamMember(index)}
                                    className="p-1 hover:bg-destructive/10 rounded transition-colors text-destructive"
                                    title={t('profile.edit.fields.removeTeamMember')}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 bg-muted/30 border-[2px] border-dashed border-dark">
                          <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                          <p className="text-muted-foreground">{t('profile.edit.fields.noTeamMembers')}</p>
                        </div>
                      )}
                      
                      {teamMembers.length >= 10 && (
                        <p className="text-sm text-muted-foreground italic mt-4">
                          {t('profile.edit.fields.maxTeamMembers')}
                        </p>
                      )}
                  </section>
                </div>
              ) : (
                <div className="text-center py-16 bg-muted/30 border-[3px] border-dark">
                  <Lock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-heading text-xl font-bold mb-2">{t('profile.edit.upgrade.title')}</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    {t('profile.edit.upgrade.teamLocked')}
                  </p>
                  <Link to="/pricing">
                    <BrutalButton size="lg">
                      {t('profile.edit.upgrade.upgradeToPlus')} →
                    </BrutalButton>
                  </Link>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      
      <Footer />
      
      
      {/* Modal Team Member */}
      <TeamMemberModal 
        isOpen={showAddTeamModal} 
        onClose={() => { 
          setShowAddTeamModal(false); 
          setEditingMemberIndex(null);
          setEditingTeamMember(null); 
        }} 
        editingMember={editingMemberIndex !== null ? teamMembers[editingMemberIndex] : null}
        onSave={handleAddTeamMember}
        userId={user?.id || ''} 
      />

    </div>
  );
};

export default ProfileEdit;
